import { useState } from "react";
import { toast } from "sonner";
import {
  DownloadSimple, Share, Plus, DotsThreeVertical, DotsThreeOutline, X, DeviceMobile, CheckCircle,
} from "@phosphor-icons/react";
import { usePWAInstall } from "../hooks/usePWAInstall";
import { Logo } from "./Logo";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-primary/15 text-primary font-mono text-xs font-bold">{n}</span>
      <span className="pt-0.5">{children}</span>
    </li>
  );
}

function Instructions({ platform }) {
  if (platform === "ios") {
    return (
      <ol className="space-y-3 text-sm" data-testid="install-steps-ios">
        <Step n={1}>Tap the <Share size={16} className="inline mx-0.5" weight="bold" /> <strong>Share</strong> icon in Safari's bottom toolbar.</Step>
        <Step n={2}>Scroll down and tap <Plus size={16} className="inline mx-0.5" weight="bold" /> <strong>Add to Home Screen</strong>.</Step>
        <Step n={3}>Tap <strong>Add</strong> — Maria's Forex / Crypto appears on your Home Screen like an app.</Step>
      </ol>
    );
  }
  if (platform === "ios-other") {
    return (
      <div className="text-sm space-y-3" data-testid="install-steps-ios-other">
        <p className="text-muted-foreground">Installing on iPhone/iPad only works from <strong>Safari</strong>.</p>
        <ol className="space-y-3">
          <Step n={1}>Open this page in <strong>Safari</strong>.</Step>
          <Step n={2}>Tap the <Share size={16} className="inline mx-0.5" weight="bold" /> <strong>Share</strong> icon.</Step>
          <Step n={3}>Choose <Plus size={16} className="inline mx-0.5" weight="bold" /> <strong>Add to Home Screen</strong>.</Step>
        </ol>
      </div>
    );
  }
  if (platform === "android") {
    return (
      <ol className="space-y-3 text-sm" data-testid="install-steps-android">
        <Step n={1}>Tap the <DotsThreeVertical size={18} className="inline" weight="bold" /> <strong>menu</strong> in your browser's top-right.</Step>
        <Step n={2}>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</Step>
        <Step n={3}>Confirm <strong>Install</strong> — the app icon is added to your Home Screen.</Step>
      </ol>
    );
  }
  return (
    <ol className="space-y-3 text-sm" data-testid="install-steps-desktop">
      <Step n={1}>Look for the <DownloadSimple size={16} className="inline mx-0.5" weight="bold" /> install icon in your browser's address bar.</Step>
      <Step n={2}>Or open the browser <DotsThreeOutline size={18} className="inline" weight="bold" /> menu and choose <strong>Install Maria's Forex / Crypto</strong>.</Step>
      <Step n={3}>Launch it any time from your desktop or app launcher.</Step>
    </ol>
  );
}

export function InstallDialog({ open, onOpenChange }) {
  const { canInstall, platform, promptInstall } = usePWAInstall();

  const doNative = async () => {
    const res = await promptInstall();
    if (res.outcome === "accepted") { toast.success("Installing Maria's Forex / Crypto…"); onOpenChange(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md" data-testid="install-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="flex h-12 w-12 items-center justify-center bg-primary text-primary-foreground shrink-0">
              <DeviceMobile size={26} weight="bold" />
            </div>
            <div>
              <DialogTitle className="font-heading text-xl">Install the App</DialogTitle>
              <DialogDescription className="text-xs">Add Maria's Forex / Crypto to your Home Screen</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-1 space-y-4">
          <ul className="grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
            <li className="flex items-center gap-1.5"><CheckCircle size={14} className="text-success" weight="fill" /> Full screen</li>
            <li className="flex items-center gap-1.5"><CheckCircle size={14} className="text-success" weight="fill" /> Home icon</li>
            <li className="flex items-center gap-1.5"><CheckCircle size={14} className="text-success" weight="fill" /> No store</li>
          </ul>

          {canInstall ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Your browser supports one-tap install.</p>
              <Button onClick={doNative} className="w-full rounded-none h-11" data-testid="install-native-btn">
                <DownloadSimple size={18} weight="bold" className="mr-2" /> Install now
              </Button>
            </div>
          ) : (
            <div className="border-t border-border pt-4">
              <Instructions platform={platform} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InstallButton() {
  const { installed, eligible } = usePWAInstall();
  const [open, setOpen] = useState(false);
  if (installed || !eligible) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="install-app-btn"
        className="hidden sm:inline-flex h-9 items-center gap-2 border border-primary/40 bg-primary/10 text-primary px-3 hover-lift text-sm font-medium">
        <DownloadSimple size={16} weight="bold" /> Install
      </button>
      <InstallDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export function InstallBanner() {
  const { installed, eligible, dismissed, dismiss } = usePWAInstall();
  const [open, setOpen] = useState(false);
  if (installed || !eligible || dismissed) return null;
  return (
    <>
      <div data-testid="install-banner"
        className="fixed bottom-0 inset-x-0 z-50 border-t border-border bg-card/95 backdrop-blur-xl px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.15)] fade-up">
        <div className="mx-auto max-w-3xl flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-primary text-primary-foreground shrink-0">
            <DeviceMobile size={20} weight="bold" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold text-sm leading-tight">Install Maria's Forex / Crypto</div>
            <div className="text-xs text-muted-foreground truncate">Add it to your Home Screen — launches like a real app.</div>
          </div>
          <Button onClick={() => setOpen(true)} data-testid="install-banner-btn" className="rounded-none h-9 shrink-0">
            <DownloadSimple size={16} weight="bold" className="mr-1.5" /> Install
          </Button>
          <button onClick={dismiss} data-testid="install-banner-dismiss" aria-label="Dismiss"
            className="text-muted-foreground hover:text-foreground p-1 shrink-0">
            <X size={18} />
          </button>
        </div>
      </div>
      <InstallDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export function InstallCard() {
  const { installed, platform, env } = usePWAInstall();
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border bg-card p-5" data-testid="install-card">
      <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4 flex items-center gap-2">
        <DeviceMobile size={14} /> Install App
      </h3>
      {installed ? (
        <p className="text-sm text-success flex items-center gap-2" data-testid="install-already">
          <CheckCircle size={16} weight="fill" /> Installed — you're using the app version.
        </p>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Add Maria's Forex / Crypto to your {env.isIOS ? "iPhone/iPad" : env.isAndroid ? "Android" : "device"} Home Screen — no App Store or Google Play needed.
          </p>
          <Button onClick={() => setOpen(true)} className="rounded-none shrink-0" data-testid="install-card-btn">
            <DownloadSimple size={16} weight="bold" className="mr-2" /> How to install
          </Button>
        </div>
      )}
      <InstallDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
